import React from "react";

export default function WhatsApp() {
  return (
    <div className="max-w-4xl mx-auto p-8">
      <h2 className="text-2xl font-bold mb-4">Jobego WhatsApp Application Privacy Policy</h2>

      <p className="text-sm text-gray-500 mb-4">Effective Date: January 15, 2026</p>

      <h3 className="text-lg font-bold mb-2">1. Introduction</h3>
      <p>
        Welcome to <strong>Jobego</strong> ("we," "our," or "us"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our{" "}
        <strong>WhatsApp application</strong> ("App"), which connects job seekers and employers through an{" "}
        <strong>AI-powered Applicant Tracking System (ATS) and job marketplace</strong>. By using the App, you agree to the terms of this Privacy Policy.
      </p>

      <h3 className="text-lg font-bold mb-2">2. Information We Collect</h3>

      <h4 className="text-md font-bold mb-2">a. Personal Information</h4>
      <p>We may collect the following types of personal information:</p>
      <ul>
        <li>
          <strong>User-Provided Information:</strong> Name, email address, phone number, professional history, resume/CV, skills, job preferences, and location.
        </li>
        <li>
          <strong>Automatically Collected Information:</strong> IP address, device type, operating system, usage data, and interaction patterns within the App.
        </li>
        <li>
          <strong>Employer-Provided Information:</strong> Company details, job postings, hiring criteria, and feedback on candidates.
        </li>
      </ul>
      <p>Our AI algorithms analyze user data to:</p>
      <ul>
        <li>Improve job matching accuracy.</li>
        <li>Generate insights for employers (e.g., candidate suitability scores).</li>
        <li>Personalize job recommendations for seekers.</li>
      </ul>

      <h3 className="text-lg font-bold mb-2">3. How We Use Your Information</h3>
      <p>We use the collected information for the following purposes:</p>
      <ul>
        <li>
          <strong>Job Matching:</strong> To connect job seekers with relevant opportunities and employers with suitable candidates.
        </li>
        <li>
          <strong>AI Optimization:</strong> To train and improve our AI models for better matching and recommendations.
        </li>
        <li>
          <strong>Communication:</strong> To send notifications, updates, and support via WhatsApp.
        </li>
        <li>
          <strong>Analytics:</strong> To monitor App performance, user engagement, and trends.
        </li>
        <li>
          <strong>Compliance:</strong> To comply with legal obligations, such as GDPR and labor laws.
        </li>
      </ul>

      <h3 className="text-lg font-bold mb-2">4. Data Sharing and Disclosure</h3>
      <p>
        We do <strong>not</strong> sell your personal data. However, we may share information with:
      </p>
      <ul>
        <li>
          <strong>Employers:</strong> Limited to job applications, resumes, and relevant profile details (with user consent).
        </li>
        <li>
          <strong>Service Providers:</strong> Third-party vendors (e.g., cloud storage, analytics tools) who assist in operating the App.
        </li>
        <li>
          <strong>Legal Authorities:</strong> If required by law or to protect our rights and safety.
        </li>
      </ul>

      <h3 className="text-lg font-bold mb-2">5. Data Security</h3>
      <p>We implement industry-standard security measures to protect your data, including:</p>
      <ul>
        <li>Encryption of sensitive information.</li>
        <li>Regular security audits.</li>
        <li>Access controls for employees and partners.</li>
      </ul>

      <h3 className="text-lg font-bold mb-2">6. User Rights (GDPR Compliance)</h3>
      <p>Under GDPR and other applicable laws, you have the right to:</p>
      <ul>
        <li>
          <strong>Access:</strong> Request a copy of your personal data.
        </li>
        <li>
          <strong>Rectify:</strong> Correct inaccurate or incomplete information.
        </li>
        <li>
          <strong>Erase:</strong> Delete your data ("right to be forgotten").
        </li>
        <li>
          <strong>Restrict Processing:</strong> Limit how we use your data.
        </li>
        <li>
          <strong>Object:</strong> Opt-out of data processing for direct marketing or profiling.
        </li>
        <li>
          <strong>Portability:</strong> Receive your data in a machine-readable format.
        </li>
      </ul>
      <p>
        To exercise these rights, contact us at <strong>support@jobego.com</strong>.
      </p>

      <h3 className="text-lg font-bold mb-2">7. Data Retention</h3>
      <p>We retain your data for as long as necessary to fulfill the purposes outlined in this policy, unless a longer retention period is required by law. For example:</p>
      <ul>
        <li>
          <strong>Job seeker profiles:</strong> Retained until deletion is requested.
        </li>
        <li>
          <strong>Employer data:</strong> Retained for the duration of the business relationship.
        </li>
      </ul>

      <h3 className="text-lg font-bold mb-2">8. Children’s Privacy</h3>
      <p>
        The App is not intended for individuals under the age of <strong>16</strong>. We do not knowingly collect data from children.
      </p>

      <h3 className="text-lg font-bold mb-2">9. WhatsApp-Specific Policies</h3>
      <ul>
        <li>
          <strong>Messaging Data:</strong> WhatsApp messages exchanged via the App are subject to WhatsApp’s{" "}
          <a href="https://www.whatsapp.com/legal/privacy-policy">Privacy Policy</a>.
        </li>
        <li>
          <strong>End-to-End Encryption:</strong> Messages are encrypted, but metadata (e.g., timestamps, participant info) may be visible to WhatsApp.
        </li>
      </ul>

      <h3 className="text-lg font-bold mb-2">10. Changes to This Policy</h3>
      <p>
        We may update this Privacy Policy periodically. We will notify you of significant changes via the App or email. Continued use of the App constitutes acceptance of the
        updated policy.
      </p>

      <h3 className="text-lg font-bold mb-2">11. Contact Us</h3>
      <p>For questions or concerns about this policy, contact us at:</p>
      <ul>
        <li>
          <strong>Email:</strong> support@jobego.co
        </li>
      </ul>
    </div>
  );
}
